import { forwardRef, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

import { ChevronDown } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Input.module.css';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
  options: SelectOption[];
  /** Rendered as an empty-valued first option ("All statuses", "Select a role"). */
  placeholder?: string;
  invalid?: boolean;
  describedBy?: string | undefined;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, invalid, describedBy, className, ...rest },
  ref,
) {
  return (
    <div className={styles.selectWrap}>
      <select
        {...rest}
        ref={ref}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        className={cx(styles.select, invalid && styles.invalid, className)}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <span className={styles.arrow} aria-hidden="true">
        <ChevronDown size={15} />
      </span>
    </div>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean;
  describedBy?: string | undefined;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, describedBy, className, ...rest },
  ref,
) {
  return (
    <textarea
      {...rest}
      ref={ref}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={describedBy}
      className={cx(styles.textarea, invalid && styles.invalid, className)}
    />
  );
});
